import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { clientSearchesApi } from "core/api";
import {
  formatClientSearchOwner,
  parseClientSearchError,
} from "core/api/client-searches/client-searches.helpers";
import {
  ClientSearchDetail,
  ClientSearchResult,
} from "core/types/client-search.types";
import { CreatePracticeDialog } from "./create-practice-dialog";
import {
  NO_ESCAPE,
  SuspendedChip,
  fmtSearchDate,
  isOwnerSuspended,
} from "./client-searches.utils";

type ChipColor = "default" | "success" | "error";

// Valori canonici di SFM (client-searches.helpers.ts li normalizza gia').
const SENTIMENT_META: Record<string, { labelKey: string; color: ChipColor }> = {
  Negative: { labelKey: "clientSearches.detail.sentiment.negative", color: "error" },
  Neutral: { labelKey: "clientSearches.detail.sentiment.neutral", color: "default" },
  Positive: { labelKey: "clientSearches.detail.sentiment.positive", color: "success" },
};

type SentimentFilter = "all" | "Negative" | "Neutral" | "Positive";

// La chiave di riga include la posizione: lo stesso link puo' comparire due volte nel
// JSON di una ricerca, e una chiave duplicata farebbe confondere le righe a React.
type Row = ClientSearchResult & { key: string };

// "Seleziona negativi" prende solo i negativi che riguardano la persona: il motore marca
// is_subject_match=false i risultati sotto la soglia di pertinenza (spesso un omonimo), e
// una diffida per un contenuto che non parla del cliente non sta in piedi. null (ricerche
// vecchie, dato assente) non esclude: l'operatore li vede e decide lui.
const isSelectableNegative = (r: Row): boolean =>
  r.sentiment?.sentiment === "Negative" && r.sentiment.isSubjectMatch !== false;

type InfoLineProps = { label: string; children: React.ReactNode };

const InfoLine: React.FC<InfoLineProps> = ({ label, children }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
    sx={{ py: 0.5 }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
      {label}
    </Typography>
    {children}
  </Stack>
);

type ResultRowProps = {
  row: Row;
  checked: boolean;
  onToggle: (link: string) => void;
};

// Riga memorizzata: con qualche centinaio di risultati, spuntarne uno non deve
// ridisegnare tutta la tabella.
function ResultRowBase({ row, checked, onToggle }: ResultRowProps) {
  const { t } = useTranslation();
  const meta = row.sentiment ? SENTIMENT_META[row.sentiment.sentiment] : undefined;
  const confidence = row.sentiment?.confidenceSubjectMatch ?? null;
  return (
    <TableRow
      hover={!row.signaled}
      selected={checked}
      onClick={row.signaled ? undefined : () => onToggle(row.link)}
      sx={{
        cursor: row.signaled ? "default" : "pointer",
        bgcolor: row.signaled ? "action.hover" : undefined,
      }}
    >
      <TableCell padding="checkbox">
        {row.signaled ? (
          // Il link e' gia' in una pratica di questo cliente: rimetterlo in un'altra
          // produrrebbe una seconda diffida per lo stesso contenuto.
          <Tooltip title={t("clientSearches.detail.signaled_tooltip")}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
              <Checkbox size="small" checked={false} disabled />
              <LockOutlinedIcon fontSize="small" color="disabled" />
            </Box>
          </Tooltip>
        ) : (
          <Checkbox
            size="small"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(row.link)}
          />
        )}
      </TableCell>
      <TableCell sx={{ maxWidth: 520 }}>
        <Link
          href={row.link}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 600, wordBreak: "break-word" }}
        >
          {row.title || row.link}
        </Link>
        {row.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.description}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ wordBreak: "break-all" }}>{row.domain || "—"}</TableCell>
      <TableCell>
        {row.sentiment && row.sentiment.sentiment ? (
          <Tooltip title={row.sentiment.summary}>
            <Chip
              size="small"
              color={meta?.color ?? "default"}
              label={meta ? t(meta.labelKey) : row.sentiment.sentiment}
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("clientSearches.detail.no_sentiment")}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {confidence === null ? "—" : `${Math.round(confidence)}%`}
      </TableCell>
    </TableRow>
  );
}

const ResultRow = memo(ResultRowBase);

/**
 * Dettaglio di una ricerca di un cliente: l'operatore sceglie i link e crea la pratica
 * intestata al PROPRIETARIO della ricerca, poi la lavora nella console Takedown.
 */
export const ClientSearchDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { searchId: searchIdParam } = useParams<{ searchId: string }>();
  const searchId = Number(searchIdParam);
  const validId = Number.isInteger(searchId) && searchId > 0;

  const [detail, setDetail] = useState<ClientSearchDetail | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Link selezionati (non le righe): due righe con lo stesso link sono la stessa URL.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>("all");
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  // Cambia a ogni apertura: il dialog rinasce pulito (vedi create-practice-dialog.tsx).
  const [dialogKey, setDialogKey] = useState(0);

  useEffect(() => {
    if (!validId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      setDetail(null);
      setRows([]);
      setSelected(new Set());
      try {
        const data = await clientSearchesApi.detail(searchId);
        if (!active) return;
        setDetail(data);
        setRows(data.results.map((r, i) => ({ ...r, key: `${i}:${r.link}` })));
      } catch (e: any) {
        // 404 search_not_found / results_missing, 409 owner_mismatch: il testo del
        // backend dice gia' cosa non va.
        if (active) {
          setError(parseClientSearchError(e, e?.message || String(e)).message);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [searchId, validId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (sentimentFilter !== "all" && r.sentiment?.sentiment !== sentimentFilter) {
        return false;
      }
      if (!q) return true;
      const hay = [r.title, r.description, r.domain, r.link, r.sentiment?.summary]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(q) !== -1;
    });
  }, [rows, query, sentimentFilter]);

  // URL da inviare: nell'ordine dei risultati, senza doppioni e mai un link gia' in
  // pratica (anche se fosse rimasto selezionato da prima di essere bloccato).
  const selectedUrls = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    rows.forEach((r) => {
      if (!r.signaled && selected.has(r.link) && !seen.has(r.link)) {
        seen.add(r.link);
        out.push(r.link);
      }
    });
    return out;
  }, [rows, selected]);

  const toggle = useCallback((link: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(link)) next.delete(link);
      else next.add(link);
      return next;
    });
  }, []);

  // Le selezioni rapide AGGIUNGONO alla selezione corrente, guardano SOLO le righe
  // visibili coi filtri attuali e saltano i link gia' in pratica (checkbox bloccata).
  // Visibili e basta: con un filtro attivo, prendere anche le righe nascoste manderebbe
  // in diffida link che l'operatore non ha mai visto.
  const selectWhere = (pred: (r: Row) => boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      visible.forEach((r) => {
        if (!r.signaled && pred(r)) next.add(r.link);
      });
      return next;
    });
  };

  const selectableNegatives = visible.filter(
    (r) => !r.signaled && isSelectableNegative(r)
  ).length;
  const selectableVisible = visible.filter((r) => !r.signaled).length;

  // Link finiti in una pratica del cliente (appena creata, o gia' presenti secondo il
  // 409 del backend): si bloccano come quelli che il backend segnala come signaled.
  const markSignaled = useCallback((links: string[]) => {
    const done = new Set(links);
    setRows((prev) =>
      prev.map((r) => (done.has(r.link) && !r.signaled ? { ...r, signaled: true } : r))
    );
    setSelected((prev) => {
      const next = new Set(prev);
      links.forEach((l) => next.delete(l));
      return next;
    });
  }, []);

  const openDialog = () => {
    setDialogKey((k) => k + 1);
    setDialogOpen(true);
  };

  const backLink = (
    <Link
      component="button"
      type="button"
      underline="hover"
      color="text.secondary"
      onClick={() => navigate("/web-rep/client-searches")}
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
    >
      <ArrowBackRoundedIcon fontSize="small" />
      {t("clientSearches.detail.back")}
    </Link>
  );

  if (!validId) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        {backLink}
        <Alert severity="error">{t("clientSearches.detail.invalid_id")}</Alert>
      </Box>
    );
  }

  const owner = detail?.owner;
  const ownerName = formatClientSearchOwner(owner);
  const ownerMissing = !!detail && !owner?.id;
  const isFiltered = sentimentFilter !== "all" || !!query.trim();

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {backLink}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("clientSearches.detail.error_load", { error, interpolation: NO_ESCAPE })}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress color="secondary" size={"80px"} />
        </Box>
      )}

      {detail && owner && (
        <>
          {/* Intestazione: chi e' stato cercato e per conto di quale cliente */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <PersonSearchOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              {detail.search.fullName || "—"}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={t("clientSearches.detail.search_id", { id: detail.search.searchId })}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("clientSearches.detail.description")}
          </Typography>

          <Paper sx={{ borderRadius: "10px", padding: "16px 20px", mb: 2 }}>
            <InfoLine label={t("clientSearches.detail.person")}>
              <Typography variant="body2" fontWeight={600}>
                {detail.search.fullName || "—"}
              </Typography>
            </InfoLine>
            <InfoLine label={t("clientSearches.detail.client")}>
              <Typography variant="body2" fontWeight={600}>
                {ownerName || "—"}
              </Typography>
              {owner.email && owner.email !== ownerName && (
                <Typography variant="body2" color="text.secondary">
                  {owner.email}
                </Typography>
              )}
              {isOwnerSuspended(owner) && <SuspendedChip />}
            </InfoLine>
            <InfoLine label={t("clientSearches.detail.date")}>
              <Typography variant="body2">
                {fmtSearchDate(detail.search.searchCreationTime, i18n.language)}
              </Typography>
            </InfoLine>
            <InfoLine label={t("clientSearches.detail.keywords")}>
              {detail.search.params.length ? (
                detail.search.params.map((p) => <Chip key={p} size="small" label={p} />)
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("clientSearches.detail.no_keywords")}
                </Typography>
              )}
            </InfoLine>
          </Paper>

          {detail.search.status === "in_progress" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("clientSearches.detail.in_progress")}
            </Alert>
          )}

          {/* Senza proprietario il backend risponde owner_missing: inutile proporre il
              bottone di creazione. */}
          {ownerMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("clientSearches.detail.owner_missing")}
            </Alert>
          )}

          <Paper sx={{ borderRadius: "10px", padding: "20px", mb: 2 }}>
            {/* Filtri */}
            <Box
              paddingBottom={"12px"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              flexWrap={"wrap"}
              gap={2}
            >
              <Typography fontSize={"16px"} fontWeight={700}>
                {isFiltered
                  ? t("clientSearches.detail.results_count_filtered", {
                      shown: visible.length,
                      total: rows.length,
                    })
                  : t("clientSearches.detail.results_count", { count: rows.length })}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={sentimentFilter}
                  onChange={(_e, v: SentimentFilter | null) => {
                    // Un secondo click sul filtro attivo non lo "spegne" in uno stato vuoto.
                    if (v) setSentimentFilter(v);
                  }}
                >
                  <ToggleButton value="all">
                    {t("clientSearches.detail.sentiment_filter.all")}
                  </ToggleButton>
                  <ToggleButton value="Negative">
                    {t("clientSearches.detail.sentiment_filter.negative")}
                  </ToggleButton>
                  <ToggleButton value="Neutral">
                    {t("clientSearches.detail.sentiment_filter.neutral")}
                  </ToggleButton>
                  <ToggleButton value="Positive">
                    {t("clientSearches.detail.sentiment_filter.positive")}
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  placeholder={t("clientSearches.detail.filter_placeholder")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{ minWidth: 240 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Box>

            {/* Selezione e creazione pratica */}
            <Box
              paddingBottom={"15px"}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              flexWrap={"wrap"}
              gap={2}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* Il tooltip dice cosa resta fuori: senza, un negativo non spuntato
                    sembrerebbe un difetto della selezione. Lo span serve perche' un
                    bottone disabilitato non riceve gli eventi del tooltip. */}
                <Tooltip title={t("clientSearches.detail.select_negative_tooltip")}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!selectableNegatives}
                      onClick={() => selectWhere(isSelectableNegative)}
                    >
                      {t("clientSearches.detail.select_negative")}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!selectableVisible}
                  onClick={() => selectWhere(() => true)}
                >
                  {t("clientSearches.detail.select_visible")}
                </Button>
                <Button
                  size="small"
                  disabled={!selected.size}
                  onClick={() => setSelected(new Set())}
                >
                  {t("clientSearches.detail.deselect_all")}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {t("clientSearches.detail.selected_count", {
                    count: selectedUrls.length,
                  })}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                disabled={!selectedUrls.length || ownerMissing}
                onClick={openDialog}
              >
                {t("clientSearches.detail.create_cta", {
                  customer: ownerName || "—",
                  interpolation: NO_ESCAPE,
                })}
              </Button>
            </Box>

            <TableContainer
              component={Paper}
              style={{
                position: "relative",
                borderRadius: "8px",
                maxHeight: "640px",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t("clientSearches.detail.table.title")}</TableCell>
                    <TableCell>{t("clientSearches.detail.table.domain")}</TableCell>
                    <TableCell>{t("clientSearches.detail.table.sentiment")}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("clientSearches.detail.table.confidence_tooltip")}>
                        <span>{t("clientSearches.detail.table.confidence")}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((r) => (
                    <ResultRow
                      key={r.key}
                      row={r}
                      checked={!r.signaled && selected.has(r.link)}
                      onToggle={toggle}
                    />
                  ))}
                  {!visible.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography
                          color="text.secondary"
                          sx={{ py: 3, textAlign: "center" }}
                        >
                          {rows.length
                            ? t("clientSearches.detail.empty_filtered")
                            : t("clientSearches.detail.empty")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <CreatePracticeDialog
            key={dialogKey}
            open={dialogOpen}
            searchId={detail.search.searchId || searchId}
            owner={owner}
            defaultCustomerName={detail.search.fullName.trim()}
            urls={selectedUrls}
            onClose={() => setDialogOpen(false)}
            onCreated={(_created, sentUrls) => markSignaled(sentUrls)}
            onAlreadyInPractice={markSignaled}
            onOpenPractice={(practiceId) =>
              navigate("/web-rep/takedown/" + practiceId)
            }
          />
        </>
      )}
    </Box>
  );
};

export default ClientSearchDetailPage;
